import { Eye } from "lucide-react"
import type { TableAction, BulkAction } from "@shared/types/data-table"
import type { Terminal } from "@features/in-stores/types/terminal.types"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"

interface TerminalSpecificHandlers {
  onView?: (terminal: Terminal) => void
  onPreview?: (terminal: Terminal) => void
  onRestart?: (terminal: Terminal) => void
  onMove?: (terminal: Terminal) => void
  onReturn?: (terminal: Terminal) => void
  onViewStore?: (terminal: Terminal) => void
  onBulkRestart?: (terminalIds: string[]) => void
}

export type TerminalActionHandlers = BaseActionHandlers<Terminal> & TerminalSpecificHandlers

const createTerminalBulkActions = (handlers: TerminalActionHandlers): BulkAction[] => [
  {
    key: "restart",
    label: "Restart Terminals",
    onClick: (ids: string[]) => handlers.onBulkRestart?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
]

// Row actions for tables - quick access operations
const createTerminalRowActions = (handlers: TerminalActionHandlers): TableAction<Terminal>[] => [
  {
    key: "preview",
    label: "Preview",
    icon: <Eye className="h-4 w-4" />,
    onClick: (terminal) => handlers.onPreview?.(terminal),
  },
  {
    key: "restart",
    label: "Restart Terminal",
    onClick: (terminal) => handlers.onRestart?.(terminal),
    condition: (terminal) => terminal.status === "active",
  },
  {
    key: "move",
    label: "Move Terminal",
    onClick: (terminal) => handlers.onMove?.(terminal),
  },
  {
    key: "view-store",
    label: "View Store",
    onClick: (terminal) => handlers.onViewStore?.(terminal),
  },
]

// Panel actions for detail pages - comprehensive operations
const createTerminalPanelActions = (handlers: TerminalActionHandlers): TableAction<Terminal>[] => [
  {
    key: "restart",
    label: "Restart Terminal",
    onClick: (terminal) => handlers.onRestart?.(terminal),
    condition: (terminal) => terminal.status === "active",
  },
  {
    key: "move",
    label: "Move Terminal",
    onClick: (terminal) => handlers.onMove?.(terminal),
  },
  {
    key: "return",
    label: "Return Terminal",
    onClick: (terminal) => handlers.onReturn?.(terminal),
  },
  {
    key: "view-store",
    label: "View Store",
    onClick: (terminal) => handlers.onViewStore?.(terminal),
  },
]

export const getTerminalActions = (handlers: TerminalActionHandlers = {}) => {
  const baseActions = createBaseActions("Terminal", handlers, {
    showEdit: false,
    showDelete: false,
    showExport: true,
  })

  const terminalSpecificActions = {
    bulkActions: createTerminalBulkActions(handlers),
    rowActions: createTerminalRowActions(handlers),
  }

  return mergeActions(baseActions, terminalSpecificActions)
}

export const getTerminalBulkActions = (handlers: TerminalActionHandlers = {}): BulkAction[] =>
  getTerminalActions(handlers).bulkActions

export const getTerminalRowActions = (handlers: TerminalActionHandlers = {}): TableAction<Terminal>[] =>
  getTerminalActions(handlers).rowActions

export const getTerminalPanelActions = (handlers: TerminalActionHandlers = {}): TableAction<Terminal>[] => {
  const baseActions = createBaseActions("Terminal", handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: true,
    showAddNote: true,
    showCopyId: true,
    showExport: true,
  })

  return mergeActions(baseActions, {
    rowActions: createTerminalPanelActions(handlers),
  }).rowActions
}

export const getTerminalHoverActions = (handlers: TerminalActionHandlers = {}): TableAction<Terminal>[] => [
  {
    key: "restart",
    label: "Restart",
    onClick: (terminal) => handlers.onRestart?.(terminal),
    condition: (terminal) => terminal.status === "active",
  },
]
